import Markdown from 'markdown-to-jsx';
import { useEffect, useState, type FC, type ReactNode, useMemo, useRef } from 'react';
import { useParams } from 'react-router';

import MonitoringMs from '../../api/monitoring';
import useApiQuery from '../../hooks/useApiQuery';

import Alert from './Alert';
import BarChartComponent from './BarChart';
import GaugeComponent from './Gauge';
import GeoMap from './GeoMap';
import Stat from './Stat';
import MonitoringTable from './Table';
import TimeSeries from './TimeSeries';

import type { GeoMapStyles, PanelData, PanelInfo, PanelTarget } from '@/types/monitoringTypes';

interface DateRange {
  from: string;
  to: string;
}

interface Styles {
  stat?: { height: number; fontSize: string };
  barchart?: { height: number };
  timeseries?: { height: number };
  gauge?: { height: number };
  table?: { height: string };
  geomap: GeoMapStyles;
}

interface PanelDataRequest {
  queries: PanelTarget[];
  to: string;
  from: string;
  filterParams: Record<string, unknown>;
}

interface MonitoringComponentsProps {
  panelInfo?: PanelInfo;
  dateRange?: DateRange;
  filterParams?: Record<string, unknown>;
  styles: Styles;
}

const MonitoringComponents: FC<MonitoringComponentsProps> = ({
  panelInfo = { type: '', targets: [] },
  dateRange = { from: '', to: '' },
  filterParams = {},
  styles,
}): ReactNode => {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const [panelData, setPanelData] = useState<PanelData | undefined>(undefined);
  const lastRequestRef = useRef<string>('');
  const requestInProgressRef = useRef(false);

  const {
    makeApiCall: getPanelDataApiCall,
    isLoading,
    error,
  } = useApiQuery<PanelData>(
    MonitoringMs.getPanel as unknown as (...args: unknown[]) => Promise<PanelData>,
    { disableGlobalLoader: true }
  );

  const filteredTargets = useMemo(() => {
    if (panelInfo.targets) {
      if (['gauge', 'geomap', 'table'].includes(panelInfo.type ?? '')) {
        return panelInfo.targets.filter(target => !target.hide);
      }
      return panelInfo.targets;
    }
    return [];
  }, [panelInfo.targets, panelInfo.type]);

  useEffect(() => {
    if (filteredTargets.length === 0) {
      setPanelData(undefined);
      return;
    }

    const toDate = new Date(dateRange.to).getTime();
    const fromDate = new Date(dateRange.from).getTime();

    const request: PanelDataRequest = {
      queries: filteredTargets,
      to: toDate.toString(),
      from: fromDate.toString(),
      filterParams,
    };

    const requestHash = JSON.stringify(request);

    // Only call if request has actually changed and no request is in progress
    if (lastRequestRef.current !== requestHash && !requestInProgressRef.current) {
      lastRequestRef.current = requestHash;
      requestInProgressRef.current = true;

      getPanelDataApiCall(request, dashboardId)
        .then(data => {
          if (data && 'results' in data) {
            setPanelData(data);
          }
        })
        .catch((err: unknown) => {
          console.error('Panel data fetch error:', err);
        })
        .finally(() => {
          requestInProgressRef.current = false;
        });
    }
  }, [
    dateRange.to,
    dateRange.from,
    filteredTargets,
    filterParams,
    getPanelDataApiCall,
    dashboardId,
  ]);

  if (error) {
    return <Alert error={error} />;
  }

  if (panelInfo.type === 'text') {
    return <Markdown>{panelInfo.content as string}</Markdown>;
  }
  if (panelInfo.type === 'stat') {
    return (
      <Stat
        panelInfo={panelInfo}
        styles={styles.stat ?? { height: 0, fontSize: '16px' }}
        isLoading={isLoading}
        panelData={panelData ?? ({} as PanelData)}
      />
    );
  }
  if (panelInfo.type === 'barchart') {
    return (
      <BarChartComponent
        panelInfo={panelInfo}
        height={styles.barchart?.height ?? 0}
        isLoading={isLoading}
        panelData={panelData}
      />
    );
  }
  if (panelInfo.type === 'timeseries') {
    return (
      <TimeSeries
        panelInfo={panelInfo}
        height={styles.timeseries?.height ?? 0}
        isLoading={isLoading}
        panelData={panelData}
      />
    );
  }
  if (panelInfo.type === 'gauge') {
    return (
      <GaugeComponent
        panelInfo={panelInfo}
        height={styles.gauge?.height ?? 0}
        isLoading={isLoading}
        panelData={panelData ?? ({} as PanelData)}
      />
    );
  }
  if (panelInfo.type === 'table') {
    return (
      <MonitoringTable
        panelInfo={panelInfo}
        styles={styles.table ?? { height: '100%' }}
        isLoading={isLoading}
        panelData={panelData ?? ({} as PanelData)}
      />
    );
  }

  if (panelInfo.type === 'geomap') {
    return (
      <GeoMap
        panelInfo={panelInfo}
        mapZoom={5}
        styles={styles.geomap}
        isLoading={isLoading}
        panelData={panelData ?? ({} as PanelData)}
      />
    );
  }
  return null;
};

export default MonitoringComponents;
